import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { BUNDLED_LANGUAGES, GENERAL_SETTINGS_STORAGE_KEY, bundleLanguageFor, defaultLocaleForLanguage, deriveSwissGerman, detectDefaultLocale, localeFromSettings, resolveLanguage, } from './locales';
const NAMESPACES = [
    'common',
    'viewer',
    'report',
    'settings',
    'teleconsultation',
    'schedule',
    'link',
];
function storedLocale() {
    try {
        return localeFromSettings(localStorage.getItem(GENERAL_SETTINGS_STORAGE_KEY));
    }
    catch {
        return null;
    }
}
/**
 * The Intl-compatible locale for every date/number/currency call: the user's
 * stored preference, else the locale implied by the active UI language, else
 * the browser's. Always a full tag (`de-CH`, never `de`), which is what makes
 * Swiss formats come out right.
 */
export function getIntlLocale() {
    const stored = storedLocale();
    if (stored)
        return stored;
    const language = i18n.language;
    if (language && language !== 'en')
        return defaultLocaleForLanguage(language);
    return detectDefaultLocale();
}
/** The UI language to boot with: from the stored preference, else the browser. */
export function getInitialLanguage() {
    return resolveLanguage(storedLocale() ?? detectDefaultLocale());
}
/** Switch the UI language to match a locale preference. No-op when unchanged. */
export async function applyLocale(locale) {
    const language = resolveLanguage(locale ?? detectDefaultLocale());
    if (i18n.language !== language)
        await i18n.changeLanguage(language);
}
/**
 * `applyLocale`, but every translated component re-renders even when the UI
 * language is unchanged (en-US → en-GB is still English). Dates, clocks and
 * numbers are formatted during render from the stored locale, so a change
 * that arrives after the first paint, such as the account preferences landing
 * at boot, would otherwise stay US-shaped until something else re-rendered.
 * i18next emits `languageChanged` from `changeLanguage` regardless of whether
 * the language differs, and react-i18next re-renders its subscribers on it.
 */
export async function refreshLocale(locale) {
    await i18n.changeLanguage(resolveLanguage(locale ?? detectDefaultLocale()));
}
/** Keep `<html lang>` honest for screen readers, hyphenation and spellcheck. */
function syncDocumentLanguage(language) {
    if (typeof document !== 'undefined' && language)
        document.documentElement.lang = language;
}
const initialLanguage = getInitialLanguage();
i18n
    .use(HttpBackend)
    .use(initReactI18next)
    .init({
    lng: initialLanguage,
    fallbackLng: 'en',
    supportedLngs: [...BUNDLED_LANGUAGES],
    // Each language loads only its own bundle: de-CH is derived from the de
    // files below, so there is nothing to fetch for the regional tag itself.
    load: 'currentOnly',
    ns: [...NAMESPACES],
    defaultNS: 'common',
    backend: {
        loadPath: (languages, namespaces) => `/locales/${bundleLanguageFor(languages[0])}/${namespaces[0]}.json`,
        parse: (data, language, namespace) => {
            const json = JSON.parse(data);
            const lng = Array.isArray(language) ? language[0] : language;
            const ns = Array.isArray(namespace) ? namespace[0] : namespace;
            return lng === 'de-CH' ? deriveSwissGerman(ns ?? '', json) : json;
        },
    },
    interpolation: {
        escapeValue: false, // React already escapes
    },
    react: {
        useSuspense: false,
    },
});
i18n.on('languageChanged', syncDocumentLanguage);
syncDocumentLanguage(initialLanguage);
// Secondary viewer/report windows share this origin's localStorage, so a locale
// change saved on one monitor reaches every open window through the storage
// event (which never fires in the window that made the change).
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
        if (event.key !== GENERAL_SETTINGS_STORAGE_KEY)
            return;
        void applyLocale(localeFromSettings(event.newValue));
    });
}
export default i18n;
